import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/backendURL';

export default function InvitationOnboarding({ inviteId, token }) {
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setError('This invitation link is incomplete. Ask your administrator for a new invitation.');
      return;
    }
    axios.get(`${BACKEND_URL}/api/team/invitations/${encodeURIComponent(inviteId)}/preview`, {
      params: { token },
    }).then(({ data }) => { if (active) setInvitation(data); }).catch((failure) => {
      if (active) setError(typeof failure.response?.data?.detail === 'string'
        ? failure.response.data.detail : 'Unable to load this invitation. Please try again.');
    });
    return () => { active = false; };
  }, [inviteId, token]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    const isNew = !invitation.account_exists && !registered;
    if (isNew && password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        await axios.post(`${BACKEND_URL}/api/team/invitations/${encodeURIComponent(inviteId)}/register`, {
          token, name, password,
        });
        setRegistered(true);
      }
      const credentials = { email: invitation.email.toLowerCase(), password };
      let response = await axios.post(`${BACKEND_URL}/api/auth/login`, credentials);
      if (!isNew && !registered) {
        await axios.get(`${BACKEND_URL}/api/team/accept-invitation/${encodeURIComponent(inviteId)}`, {
          params: { token }, headers: { Authorization: `Bearer ${response.data.access_token}` },
        });
        setRegistered(true);
        // Reload membership and workspace after joining, rather than using pre-join data.
        response = await axios.post(`${BACKEND_URL}/api/auth/login`, credentials);
      }
      const { access_token: accessToken, workspace } = response.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('token_domain', window.location.hostname);
      const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      window.location.assign(!local && workspace?.url
        ? `${workspace.url}/?auth_token=${encodeURIComponent(accessToken)}` : '/');
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to accept this invitation. Please try again.');
      if (failure.response?.status === 409 && String(detail).includes('account already exists')) {
        setInvitation((current) => ({ ...current, account_exists: true }));
      }
    } finally {
      setBusy(false);
    }
  };

  const isNew = invitation && !invitation.account_exists && !registered;
  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <section className="w-full max-w-md rounded-xl bg-white border p-8 space-y-5" aria-label="Team invitation">
        <h1 className="text-2xl font-semibold">Join your team</h1>
        {error && <p role="alert" className="text-red-700">{error}</p>}
        {!invitation && !error && <p role="status">Loading invitation…</p>}
        {invitation && <>
          <p>You have been invited to <strong>{invitation.organization_name}</strong> as {invitation.role}.</p>
          <p>{isNew ? 'Create your password to join this workspace.' : 'Sign in with your existing account to continue.'}</p>
          <form onSubmit={submit} className="space-y-4">
            <label className="block">Email
              <input className="w-full border rounded p-2 bg-slate-50" type="email" value={invitation.email} readOnly autoComplete="username" />
            </label>
            {isNew && <label className="block">Full name
              <input className="w-full border rounded p-2" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoComplete="name" />
            </label>}
            <label className="block">{isNew ? 'Create password' : 'Password'}
              <input className="w-full border rounded p-2" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={isNew ? 8 : 1} maxLength={72} autoComplete={isNew ? 'new-password' : 'current-password'} />
            </label>
            {isNew && <label className="block">Confirm password
              <input className="w-full border rounded p-2" type="password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required autoComplete="new-password" />
            </label>}
            <button className="w-full bg-indigo-600 text-white rounded p-3 disabled:opacity-50" disabled={busy} type="submit">
              {busy ? 'Joining…' : isNew ? 'Create account and join' : 'Sign in and join'}
            </button>
          </form>
          <p className="text-sm text-slate-600">Your team manages your workspace and subscription.</p>
        </>}
        <a className="block text-indigo-700" href="/">Back to Lexa.run</a>
      </section>
    </main>
  );
}
